import { useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import Flex from "../../../../../../../../../../components/Flex";
import Text from "../../../../../../../../../../components/Text";
import { useDebounce } from "../../../../../../../../../../hooks/use-debounce";
import { useReduceDisclosure } from "../../../../../../../../../../hooks/use-reduce-disclosure";
import { useGetAllScreeningPreview } from "../../../../../../../../../../queries/encounter/screening/use-get-all-screening-preview";
import { useRosPeTemplateArchive } from "../../../../../../../../../../queries/encounter/use-ros-pe-template-archive";
import { useRosPeTemplateList } from "../../../../../../../../../../queries/encounter/use-ros-pe-template-list";
import { useGetAllScreenerType } from "../../../../../../../../../../queries/patient-chart/assessment/assement-forms/use-get-all-screener-type";
import { usePatientVitalsList } from "../../../../../../../../../../queries/vitals/use-patient-vitals-list";
import {
  DEF_PAGINATION,
  getXtenantId,
  replaceUnderScoreAddSpace,
} from "../../../../../../../../../../utils/utils";
import AssessmentDrawer from "../../../../../../../scheduling/appointment/encounter/Assessment/AssessmentDrawer";
import PeImportDrawer from "../../../../../../../scheduling/appointment/encounter/default-forms/PeImportDrawer";
import RosImportDrawer from "../../../../../../../scheduling/appointment/encounter/default-forms/RosImportDrawer";
import { StyleAddButton } from "../../../../../../../scheduling/appointment/encounter/default-forms/form-1/StyledAddButton";
import StyledPopoverButton from "../../../../../../../scheduling/appointment/encounter/default-forms/form-1/StyledPopoverButton";
import PreviewScreener from "../../../../../../../scheduling/appointment/encounter/default-forms/form-2/ScreenerPreview";
import UpsertVitalFormDrawerNew from "../../../../vitals/UpsertVitalFormDrawerNew";
import PreviewContentWrapper from "./PreviewContentWrapper";
import VitalsCommonTable from "./VitalsCommonTable";
import { formatHomeCareSummary } from "@/template/_shared/home-care-summary";

const PsychiatricNoteObjectives = () => {
  const [
    addVitalDisclosure,
    templateDisclosure,
    psychDisclosure,
    rosTemplateDisclosure,
  ] = useReduceDisclosure(4);
  const appointmentid = useSearchParams()[0].get("appointmentid");

  const [search, setSearchStringKey, instantSearchStringKey] =
    useDebounce<string>("");
  const { data: screenerTypeList } = useGetAllScreenerType({
    pagination: {
      page: 0,
      pageSize: 5,
    },
    xTENANTID: getXtenantId(),
    searchString: search,
  });

  const Screener_List = useMemo(() => {
    return screenerTypeList?.data?.content?.map((item: any) => ({
      value: item,
      label: replaceUnderScoreAddSpace(item),
    }));
  }, [screenerTypeList]);

  const { data: screeners } = useGetAllScreeningPreview({
    pagination: DEF_PAGINATION,
    gridSorting: [],
    appointmentUuid: appointmentid ?? "",
    xTENANTID: getXtenantId(),
  });

  const { mutateAsync: archiveTemplate } = useRosPeTemplateArchive();
  function templateArchive<T>(value: boolean, id: T) {
    return archiveTemplate({
      encounterTemplateId: id?.toString() ?? "",
      status: value,
      xTENANTID: getXtenantId(),
    }).then((res) => {
      refetchPeTemplates();
      return res;
    });
  }
  const { data: peTemplates, refetch: refetchPeTemplates } =
    useRosPeTemplateList({
      pagination: {
        page: 0,
        pageSize: 50,
      },
      gridSorting: [],
      archive: false,
      status: true,
      templateType: "PHYSICAL_EXAM",
      appointmentUuid: appointmentid ?? undefined,
      xTENANTID: getXtenantId(),
    });
  const filteredPeTemplates = peTemplates?.data?.content?.filter(
    (d: any) => !d.archive && !!d?.description?.toString().trim()
  );
  const { data: rosTemplates, refetch: refetchRosTemplates } =
    useRosPeTemplateList({
      pagination: { page: 0, pageSize: 50 },
      gridSorting: [],
      archive: false,
      status: true,
      templateType: "ROS",
      appointmentUuid: appointmentid ?? undefined,
      xTENANTID: getXtenantId(),
    });

  function rosTemplateArchive<T>(value: boolean, id: T) {
    return archiveTemplate({
      encounterTemplateId: id?.toString() ?? "",
      status: value,
      xTENANTID: getXtenantId(),
    }).then((res) => {
      refetchRosTemplates();
      return res;
    });
  }
  const filteredRosTemplates = rosTemplates?.data?.content?.filter(
    (d: any) => !d.archive && !!d?.description?.toString().trim()
  );
  const { data: vitalsList } = usePatientVitalsList({
    pagination: DEF_PAGINATION,
    gridSorting: [],
    archive: false,
    appointmentUuid: appointmentid ?? undefined,
  });

  return (
    <>
      {<VitalsCommonTable vitalsList={vitalsList?.data?.content} />}
      {!!filteredPeTemplates?.length && (
        <PreviewContentWrapper
          dataList={filteredPeTemplates}
          k="peTemplate"
          title="PE Templates"
          renderContent={(peTemplate: any) => (
            <Text sx={{ color: "#424242" }}>
              {`${peTemplate?.templateNameType ?? "-"} : ${formatHomeCareSummary(peTemplate?.templateNameType, peTemplate?.description) ?? "-"}`}
            </Text>
          )}
          onDelete={(uuid) => {
            templateArchive(true, uuid).then(() => {});
          }}
          onEdit={(peTemplate) => {
            templateDisclosure.open(peTemplate);
          }}
        />
      )}
      {!!filteredRosTemplates?.length && (
        <PreviewContentWrapper
          dataList={filteredRosTemplates}
          k="rosTemplate"
          title="ROS Templates"
          renderContent={(rosTemplate: any) => (
            <Text sx={{ color: "#424242" }}>
              {`${rosTemplate?.templateNameType ?? "-"} : ${formatHomeCareSummary(rosTemplate?.templateNameType, rosTemplate?.description) ?? "-"}`}
            </Text>
          )}
          onDelete={(uuid) => {
            rosTemplateArchive(true, uuid).then(() => {});
          }}
          onEdit={(rosTemplate) => {
            rosTemplateDisclosure.open(rosTemplate);
          }}
        />
      )}

      {!!Object.keys((screeners?.data ?? {}) as any)?.length && (
        <Flex flexDirection={"column"} width={"100%"} gap={1}>
          <Text fontWeight="bold" color="#424242" mb={1} ml={1}>
            Screening Assessments
          </Text>
          <PreviewScreener
            onEdit={(it) => {
              psychDisclosure.open(it);
            }}
            dataListObj={screeners?.data}
          />
        </Flex>
      )}

      {/* Action Buttons */}
      <Flex gap={1} flexWrap="wrap" sx={{ mb: 1 }}>
        <StyleAddButton
          w="100px"
          onClick={() => {
            addVitalDisclosure.open();
          }}
          label="Add Vitals"
        />
        {(rosTemplates?.data?.content?.length ?? 0) < 1 && (
          <StyleAddButton
            w="100px"
            onClick={() => rosTemplateDisclosure.open()}
            label="Add ROS"
          />
        )}
        {/* <StyleAddButton w="80px" onClick={() => {}} label="Results" /> */}
        {(peTemplates?.data?.content?.length ?? 0) < 1 && (
          <StyleAddButton
            w="80px"
            onClick={() => templateDisclosure.open()}
            label="Add PE"
          />
        )}
        {/* note: this button is replaced from  psych template */}
        <StyledPopoverButton
          disableStartIcon
          onClick={(v) => psychDisclosure.open(v)}
          options={Screener_List ?? []}
          label="Add Screening"
          instantSearchStringKey={instantSearchStringKey}
          setSearchStringKey={setSearchStringKey}
        />
      </Flex>
      <UpsertVitalFormDrawerNew disclosureHost={addVitalDisclosure} />
      <PeImportDrawer disclosureHost={templateDisclosure} />
      <AssessmentDrawer disclosureHost={psychDisclosure} />
      <RosImportDrawer disclosureHost={rosTemplateDisclosure} />
    </>
  );
};

export default PsychiatricNoteObjectives;
